import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, useCart } from "@/App";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Minus, Plus, ShoppingBag, ArrowLeft } from "lucide-react";

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${id}`);
        setProduct(response.data);
        if (response.data.sizes?.length > 0) {
          setSelectedSize(response.data.sizes[0]);
        }
        if (response.data.colors?.length > 0) {
          setSelectedColor(response.data.colors[0]);
        }
      } catch (error) {
        console.error("Error fetching product:", error);
        toast.error("Product not found");
        navigate("/shop");
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, navigate]);

  const handleAddToCart = async () => {
    if (!user) {
      toast.error("Please sign in to add items to cart");
      navigate("/auth");
      return;
    }

    if (product.sizes?.length > 0 && !selectedSize) {
      toast.error("Please select a size");
      return;
    }

    setAdding(true);
    try {
      await addToCart(product.id, quantity, selectedSize, selectedColor);
      toast.success("Added to cart!");
    } catch (error) {
      toast.error("Failed to add to cart");
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="aspect-square skeleton rounded-lg"></div>
            <div className="space-y-6">
              <div className="h-8 w-32 skeleton rounded"></div>
              <div className="h-12 w-3/4 skeleton rounded"></div>
              <div className="h-24 skeleton rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen pt-32 pb-24 px-4 sm:px-6 lg:px-8" data-testid="product-page">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#888] hover:text-[#39FF14] transition-colors mb-8"
          data-testid="back-btn"
        >
          <ArrowLeft size={20} />
          <span className="label-text">Back</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image */}
          <div className="relative aspect-square overflow-hidden bg-[#0A0A0A]">
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-cover"
            />
            {/* Badges */}
            <div className="absolute top-4 left-4 flex flex-col gap-2">
              {product.is_new_arrival && (
                <Badge className="bg-[#39FF14] text-black label-text hover:bg-[#39FF14]">
                  New Arrival
                </Badge>
              )}
              {product.is_featured && (
                <Badge className="bg-[#FF00FF] text-white label-text hover:bg-[#FF00FF]">
                  Featured
                </Badge>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="lg:py-8">
            <span className="label-text text-[#39FF14] text-sm tracking-[0.3em]">
              {product.category.toUpperCase()}
              {product.subcategory && ` / ${product.subcategory.toUpperCase()}`}
            </span>

            <h1 className="mt-4 text-3xl sm:text-4xl heading text-[#EDEDED]" data-testid="product-name">
              {product.name}
            </h1>

            <p className="mt-4 text-4xl text-[#39FF14] heading" data-testid="product-price">
              ${product.price.toFixed(2)}
            </p>

            <p className="mt-6 text-[#888] text-lg leading-relaxed">
              {product.description}
            </p>

            {/* Size Selector */}
            {product.sizes && product.sizes.length > 0 && (
              <div className="mt-8">
                <h3 className="label-text text-[#888] text-sm mb-4">Size</h3>
                <div className="flex flex-wrap gap-3">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`size-btn ${selectedSize === size ? "selected" : ""}`}
                      data-testid={`size-${size}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Color Selector */}
            {product.colors && product.colors.length > 0 && (
              <div className="mt-8">
                <h3 className="label-text text-[#888] text-sm mb-4">
                  Color: <span className="text-[#EDEDED]">{selectedColor}</span>
                </h3>
                <div className="flex flex-wrap gap-3">
                  {product.colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      className={`px-4 py-2 border text-sm transition-colors ${
                        selectedColor === color
                          ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10"
                          : "border-[#333] text-[#EDEDED] hover:border-[#888]"
                      }`}
                      data-testid={`color-${color}`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="mt-8">
              <h3 className="label-text text-[#888] text-sm mb-4">Quantity</h3>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="qty-btn"
                  data-testid="qty-decrease"
                >
                  <Minus size={18} />
                </button>
                <span className="text-xl text-[#EDEDED] w-12 text-center" data-testid="qty-value">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="qty-btn"
                  data-testid="qty-increase"
                >
                  <Plus size={18} />
                </button>
              </div>
            </div>

            {/* Add to Cart */}
            <div className="mt-10">
              <button
                onClick={handleAddToCart}
                disabled={adding || product.stock === 0}
                className="w-full btn-skew bg-[#39FF14] text-black px-8 py-4 font-bold uppercase tracking-wider flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="add-to-cart-btn"
              >
                <ShoppingBag size={20} className="transform skewX-[12deg]" />
                <span>{adding ? "Adding..." : product.stock === 0 ? "Out of Stock" : "Add to Cart"}</span>
              </button>
            </div>

            {/* Stock Info */}
            {product.stock > 0 && product.stock < 10 && (
              <p className="mt-4 text-[#FF00FF] label-text">
                Only {product.stock} left in stock!
              </p>
            )}

            {/* Additional Info */}
            <div className="mt-10 pt-10 border-t border-[#222] space-y-4">
              <div className="flex items-center gap-3 text-[#888]">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Free shipping on orders over $100</span>
              </div>
              <div className="flex items-center gap-3 text-[#888]">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Easy returns within 30 days</span>
              </div>
              <div className="flex items-center gap-3 text-[#888]">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span>Secure checkout with Zelle, Cash App & Venmo</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
